import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  IconButton,
  Alert,
  Stack,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Photo as PhotoIcon,
} from '@mui/icons-material';
import { PhotoPosition } from '../../types';

const MAX_PHOTO_POSITIONS = 8;

interface PhotoPositionEditorProps {
  size: string;
  photoPositions: PhotoPosition[];
  onChange: (positions: PhotoPosition[]) => void;
}

const PhotoPositionEditor: React.FC<PhotoPositionEditorProps> = ({
  size,
  photoPositions,
  onChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [positions, setPositions] = useState<PhotoPosition[]>(photoPositions);
  const [errors, setErrors] = useState<Record<number, string>>({});

  const getFrameDimensions = (frameSize: string) => {
    const sizeMap: Record<string, { width: number; height: number }> = {
      '2x4': { width: 2000, height: 4000 },
      '4x2': { width: 4000, height: 2000 },
      '2x6': { width: 2000, height: 6000 },
      '6x2': { width: 6000, height: 2000 },
      '4x6': { width: 4000, height: 6000 },
      '6x4': { width: 6000, height: 4000 },
      '3x4': { width: 3000, height: 4000 },
      '4x3': { width: 4000, height: 3000 },
    };
    return sizeMap[frameSize] || { width: 2000, height: 4000 };
  };

  const frameDimensions = getFrameDimensions(size);

  const previewScale = useMemo(() => {
    const maxW = isMobile ? 200 : 280;
    const maxH = isMobile ? 260 : 360;
    return Math.min(maxW / frameDimensions.width, maxH / frameDimensions.height);
  }, [frameDimensions.height, frameDimensions.width, isMobile]);

  useEffect(() => {
    setPositions(photoPositions);
  }, [photoPositions]);

  useEffect(() => {
    onChange(positions);
  }, [positions, onChange]);

  const addPosition = () => {
    if (positions.length >= MAX_PHOTO_POSITIONS) return;

    const newPosition: PhotoPosition = {
      x: 100,
      y: 100,
      width: Math.floor(frameDimensions.width * 0.3),
      height: Math.floor(frameDimensions.height * 0.3),
    };

    setPositions([...positions, newPosition]);
  };

  const removePosition = (index: number) => {
    setPositions(positions.filter((_, i) => i !== index));
    const newErrors = { ...errors };
    delete newErrors[index];
    setErrors(newErrors);
  };

  const updatePosition = (index: number, field: keyof PhotoPosition, value: number) => {
    const newPositions = [...positions];
    newPositions[index] = { ...newPositions[index], [field]: value };
    setPositions(newPositions);
    validatePosition(index, newPositions[index], newPositions);
  };

  const validatePosition = (index: number, position: PhotoPosition, all = positions) => {
    const newErrors = { ...errors };

    if (position.x < 0 || position.y < 0 || position.width <= 0 || position.height <= 0) {
      newErrors[index] = 'All values must be positive';
      setErrors(newErrors);
      return;
    }

    if (position.x + position.width > frameDimensions.width) {
      newErrors[index] = `Photo extends beyond frame width (${frameDimensions.width}px)`;
      setErrors(newErrors);
      return;
    }

    if (position.y + position.height > frameDimensions.height) {
      newErrors[index] = `Photo extends beyond frame height (${frameDimensions.height}px)`;
      setErrors(newErrors);
      return;
    }

    for (let i = 0; i < all.length; i++) {
      if (i === index) continue;
      const other = all[i];
      if (
        position.x < other.x + other.width &&
        position.x + position.width > other.x &&
        position.y < other.y + other.height &&
        position.y + position.height > other.y
      ) {
        newErrors[index] = 'Photo overlaps with another photo';
        setErrors(newErrors);
        return;
      }
    }

    delete newErrors[index];
    setErrors(newErrors);
  };

  const getPreviewStyle = (position: PhotoPosition) => ({
    position: 'absolute' as const,
    left: position.x * previewScale,
    top: position.y * previewScale,
    width: position.width * previewScale,
    height: position.height * previewScale,
    border: `1.5px solid ${alpha(theme.palette.primary.main, 0.55)}`,
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    borderRadius: 4,
    boxSizing: 'border-box' as const,
  });

  const softFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      bgcolor: alpha(theme.palette.primary.main, 0.05),
      '& fieldset': {
        borderColor: alpha(theme.palette.primary.main, 0.16),
      },
      '&:hover': {
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        '& fieldset': {
          borderColor: alpha(theme.palette.primary.main, 0.4),
        },
      },
      '&.Mui-focused': {
        bgcolor: '#fff',
        '& fieldset': {
          borderColor: 'primary.main',
          borderWidth: 1.5,
        },
      },
      '& .MuiInputBase-input': {
        py: 1.1,
        px: 1.25,
        fontWeight: 600,
        fontSize: '0.85rem',
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 700,
      fontSize: '0.8rem',
    },
  };

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography fontWeight={800} sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
            Photo Positions
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {positions.length}/{MAX_PHOTO_POSITIONS} vị trí
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={!isMobile ? <AddIcon /> : undefined}
          onClick={addPosition}
          disabled={positions.length >= MAX_PHOTO_POSITIONS}
          sx={{
            flexShrink: 0,
            textTransform: 'none',
            fontWeight: 800,
            borderRadius: 999,
            boxShadow: 'none',
            px: { xs: 1.5, sm: 2 },
            minWidth: { xs: 40, sm: 'auto' },
            ...(isMobile ? { width: 40, height: 36, minWidth: 40, p: 0 } : null),
          }}
          aria-label="Add Photo Position"
        >
          {isMobile ? <AddIcon fontSize="small" /> : 'Thêm vị trí'}
        </Button>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.25, sm: 2 },
          mb: 2,
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{ display: 'block', mb: 1 }}
        >
          Preview · {frameDimensions.width}×{frameDimensions.height}px
        </Typography>
        <Box
          sx={{
            width: frameDimensions.width * previewScale,
            height: frameDimensions.height * previewScale,
            maxWidth: '100%',
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: '#fff',
            position: 'relative',
            mx: 'auto',
            borderRadius: 1.5,
            boxShadow: `inset 0 0 0 1px ${alpha('#000', 0.03)}`,
          }}
        >
          {positions.map((position, index) => (
            <Box
              key={index}
              sx={getPreviewStyle(position)}
              title={`Photo ${index + 1}: ${position.width}x${position.height} at (${position.x}, ${position.y})`}
            />
          ))}
        </Box>
      </Paper>

      {positions.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2, py: 0.5 }}>
          Chưa có vị trí ảnh. Bấm + để thêm.
        </Alert>
      ) : (
        <Stack spacing={1.25}>
          {positions.map((position, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: { xs: 1.25, sm: 1.75 },
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: errors[index] ? 'error.light' : 'divider',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <PhotoIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  <Typography fontWeight={800} sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                    Vị trí {index + 1}
                  </Typography>
                </Stack>
                <IconButton
                  color="error"
                  onClick={() => removePosition(index)}
                  size="small"
                  sx={{
                    border: '1px solid',
                    borderColor: alpha(theme.palette.error.main, 0.25),
                    borderRadius: 2,
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>

              <Grid container spacing={1}>
                {(
                  [
                    { field: 'x' as const, label: 'X', max: frameDimensions.width },
                    { field: 'y' as const, label: 'Y', max: frameDimensions.height },
                    { field: 'width' as const, label: 'W', max: frameDimensions.width },
                    { field: 'height' as const, label: 'H', max: frameDimensions.height },
                  ] as const
                ).map((col) => (
                  <Grid item xs={6} sm={3} key={col.field}>
                    <Typography
                      sx={{
                        display: 'block',
                        mb: 0.5,
                        fontSize: '0.65rem',
                        fontWeight: 800,
                        letterSpacing: '0.05em',
                        color: 'text.secondary',
                      }}
                    >
                      {col.label}
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      value={position[col.field]}
                      onChange={(e) =>
                        updatePosition(
                          index,
                          col.field,
                          parseInt(e.target.value) || (col.field === 'width' || col.field === 'height' ? 1 : 0)
                        )
                      }
                      size="small"
                      inputProps={{
                        min: col.field === 'x' || col.field === 'y' ? 0 : 1,
                        max: col.max,
                        inputMode: 'numeric',
                      }}
                      sx={softFieldSx}
                    />
                  </Grid>
                ))}
              </Grid>

              {errors[index] && (
                <Alert severity="error" sx={{ mt: 1, borderRadius: 2, py: 0 }}>
                  {errors[index]}
                </Alert>
              )}
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default PhotoPositionEditor;
